import { useAuthReady } from './useAuthReady';
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchMe } from '../lib/me';
import { applyUserLanguage } from '../i18n';

/** Current user's profile + admin-set links. Mutations live in lib/me.ts —
 *  call them with `initData`, then `refresh()` to re-read. */
export function useMe() {
  const { canFetch: ready, initData, cacheUserId: userId } = useAuthReady();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['me', userId],
    enabled: ready,
    queryFn: () => fetchMe(initData),
    // Persisted data is only a fast first paint; always verify it with the API
    // when the current account mounts.
    refetchOnMount: 'always',
  });

  // Re-apply the saved preference whenever /me resolves so an English Telegram
  // WebView (navigator.language === 'en') cannot reset the in-app choice.
  useEffect(() => {
    const lang = q.data?.user?.language;
    if (lang) applyUserLanguage(lang);
  }, [q.data?.user?.language]);

  return {
    me: q.data?.user ?? null,
    links: q.data?.links ?? null,
    faq: q.data?.faq ?? [],
    loading: q.isLoading,
    initData,
    /** True when TMA initData or web bearer is available for API calls. */
    canFetch: ready,
    refresh: () => qc.invalidateQueries({ queryKey: ['me'] }),
  };
}
